import { Link } from 'react-router-dom'
import {ArrowLeft} from 'lucide-react'
import { useLang } from '@/context/LanguageContext'
import Seo from '@/components/Seo'

export default function PrivacyPolicy() {
  const { t, lang } = useLang()

  const sectionTitleStyle = {
    color: '#0D0C0B',
    fontSize: '20px',
    fontWeight: 300 as const,
    letterSpacing: '4px'
  }

  const bodyStyle = { color: '#82391F', fontSize: '17px', lineHeight: '1.8' }

  return (
    <div style={{ backgroundColor: '#F8F2EB', minHeight: '100vh' }}>
      <Seo
        title="Politique de confidentialité — Clips'On"
        description="Collecte et traitement des données personnelles sur clips-on.ch, conformément à la nLPD suisse et au RGPD. Finalités, durées, droits des personnes."
        path="/privacy-policy"
      />
      {/* Header/Navigation */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 transition-colors"
          style={{ color: '#82391F', fontSize: '15px' }}
          onMouseEnter={e => (e.currentTarget.style.color = '#E1593F')}
          onMouseLeave={e => (e.currentTarget.style.color = '#82391F')}
        >
          <ArrowLeft size={18} strokeWidth={1.5} />
          {t("Retour à l'accueil", "Back to home")}
        </Link>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 pb-20">
        <h1
          className="mb-4 tracking-[0.25em] uppercase"
          style={{
            color: '#0D0C0B',
            fontSize: '32px',
            fontWeight: 300,
            letterSpacing: '4px'
          }}
        >
          {t("Politique de confidentialité", "Privacy Policy")}
        </h1>
        <p className="mb-4" style={{ color: '#82391F', fontSize: '15px', opacity: 0.6 }}>
          {t("Date d'émission et de prise d'effet : avril 2026", "Issue and effective date: April 2026")}
        </p>

        <div style={bodyStyle} className="mb-16">
          <p className="mb-4">
            {t(
              "Frédéric Haenni | Clips'ON propose des boutons vestimentaires innovants, vendus en ligne via le site clips-on.ch.",
              "Frédéric Haenni | Clips'ON offers innovative clothing buttons, sold online via the website clips-on.ch."
            )}
          </p>
          <p className="mb-4">
            {t(
              "Votre vie privée et la sécurité de vos données sont importantes pour nous. Le présent document décrit les types d'informations qui sont ou peuvent être collectées, utilisées ou partagées par clips-on.ch lorsque vous le visitez, l'utilisez ou y effectuez des achats.",
              "Your privacy and data security are important to us. This document describes the types of information that are or may be collected, used, or shared by clips-on.ch when you visit, use, or make purchases on our website."
            )}
          </p>
          <p className="mb-4">
            {t(
              "En utilisant notre site, vous consentez à la présente Politique de confidentialité et acceptez ses conditions.",
              "By using our website, you consent to this Privacy Policy and accept its terms."
            )}
          </p>
          <p>
            {t(
              "Si vous avez des questions supplémentaires ou si vous souhaitez obtenir plus d'informations sur notre Politique de confidentialité ou sur la manière dont nous traitons vos données personnelles, ou si vous souhaitez retirer votre consentement, n'hésitez pas à nous contacter.",
              "If you have any additional questions or would like more information about our Privacy Policy or how we process your personal data, or if you wish to withdraw your consent, please do not hesitate to contact us."
            )}
          </p>
        </div>

        <div className="space-y-12">
          {/* I. Définitions */}
          <section>
            <h2 className="mb-4 tracking-[0.2em] uppercase" style={sectionTitleStyle}>
              {t("I. Définitions", "I. Definitions")}
            </h2>
            <div style={bodyStyle} className="space-y-3 pl-6">
              <p>
                <strong>{t("Clips'ON / Nous / Notre / Nos", "Clips'ON / We / Our / Us")} :</strong>{" "}
                {t(
                  "l'entreprise individuelle Frédéric Haenni | Clips'ON, dont le siège est sis CH-1206 Genève, opératrice du Site.",
                  "the sole proprietorship Frédéric Haenni | Clips'ON, registered at, CH-1206 Geneva, Switzerland, operator of the Website."
                )}
              </p>
              <p>
                <strong>{t("Données personnelles ou Données", "Personal Data or Data")} :</strong>{" "}
                {t(
                  "toutes les informations qui se rapportent à une personne identifiée ou identifiable (art. 5 let. a de la Loi fédérale sur la protection des données ; nLPD ; RS 235.1).",
                  "any information relating to an identified or identifiable person (Art. 5(a) of the Swiss Federal Act on Data Protection; nFADP; SR 235.1)."
                )}
              </p>
              <p>
                <strong>{t("Politique de confidentialité", "Privacy Policy")} :</strong>{" "}
                {t(
                  "le présent document permettant de connaître la nature des Données personnelles collectées par Clips'ON et leurs finalités d'utilisation.",
                  "this document describing the nature of Personal Data collected by Clips'ON and the purposes of their use."
                )}
              </p>
              <p>
                <strong>{t("Produits", "Products")} :</strong>{" "}
                {t(
                  "les boutons vestimentaires Clips'ON et accessoires associés vendus sur le Site.",
                  "Clips'ON clothing buttons and associated accessories sold on the Website."
                )}
              </p>
              <p>
                <strong>{t("Site", "Website")} :</strong>{" "}
                {t("le site accessible à l'URL suivante : https://clips-on.ch/", "the website accessible at the following URL: https://clips-on.ch/")}
              </p>
              <p>
                <strong>{t("Traitement / traiter", "Processing / process")} :</strong>{" "}
                {t(
                  "toute opération relative à des données personnelles — quels que soient les moyens et procédés utilisés — notamment la collecte, la conservation, l'exploitation, la modification, la communication, l'archivage ou la destruction de données (art. 5 let. d nLPD).",
                  "any operation relating to personal data — regardless of the means and procedures used — including the collection, storage, use, modification, disclosure, archiving, or destruction of data (Art. 5(d) nFADP)."
                )}
              </p>
              <p>
                <strong>{t("Utilisateur", "User")} :</strong>{" "}
                {t("toute personne accédant et naviguant sur le Site.", "any person accessing and browsing the Website.")}
              </p>
              <p>
                <strong>{t("Vous / Votre / Vos", "You / Your")} :</strong>{" "}
                {t("un terme désignant tout Utilisateur.", "a term designating any User.")}
              </p>
            </div>
          </section>

          {/* II. But et champ d'application */}
          <section>
            <h2 className="mb-4 tracking-[0.2em] uppercase" style={sectionTitleStyle}>
              {t("II. But et champ d'application", "II. Purpose and scope")}
            </h2>
            <div style={bodyStyle} className="space-y-3">
              <p>
                {t(
                  "La présente Politique de confidentialité vise à vous informer des conditions du Traitement des Données personnelles vous concernant. Clips'ON, qui agit en tant que responsable du Traitement, est consciente de la confiance que vous lui témoignez en lui transmettant des Données.",
                  "This Privacy Policy aims to inform you of the conditions under which your Personal Data is Processed. Clips'ON, acting as the data controller, is aware of the trust you place in us when transmitting your Data."
                )}
              </p>
              <p>
                {t(
                  "La présente Politique s'applique à tout Traitement de Données personnelles collectées en lien avec votre utilisation du Site. Elle vise également à vous informer de vos droits s'agissant du Traitement de Données personnelles et des modalités d'exercice de ces droits.",
                  "This Privacy Policy applies to all Processing of Personal Data collected in connection with your use of the Website. It also aims to inform you of your rights regarding the Processing of Personal Data and the ways in which those rights may be exercised."
                )}
              </p>
              <p>
                {t(
                  "Vos Données personnelles, dans le but de protéger votre vie privée, ne seront traitées que pour les finalités mentionnées dans la présente Politique. Clips'ON s'engage à mettre en œuvre des mesures techniques et organisationnelles visant à assurer la confidentialité et la sécurité de vos Données personnelles.",
                  "Your Personal Data, in order to protect your privacy, will only be Processed for the purposes set out in this Privacy Policy. Clips'ON undertakes to implement technical and organisational measures to ensure the confidentiality and security of your Personal Data."
                )}
              </p>
              <p>
                {t(
                  "Clips'ON est susceptible de vous suggérer des liens vers d'autres sites Internet administrés par des tiers (notamment Stripe, les réseaux sociaux, ou d'autres partenaires). Dans ce cas, Clips'ON n'a aucune maîtrise sur ces sites tiers et n'effectue aucune vérification particulière. La présente Politique de confidentialité ne s'applique pas à ces sites tiers. Toute responsabilité de Clips'ON pour avoir suggéré un lien vers un site Internet administré par un tiers est expressément exclue dans toute la mesure permise par la loi.",
                  "Clips'ON may suggest links to other websites operated by third parties (including Stripe, social media platforms, or other partners). In such cases, Clips'ON has no control over these third-party websites and does not carry out any particular verification. This Privacy Policy does not apply to these third-party websites. Any liability of Clips'ON for having suggested a link to a third-party website is expressly excluded to the fullest extent permitted by law."
                )}
              </p>
            </div>
          </section>

          {/* III. Acceptation et modification */}
          <section>
            <h2 className="mb-4 tracking-[0.2em] uppercase" style={sectionTitleStyle}>
              {t("III. Acceptation et modification", "III. Acceptance and modification")}
            </h2>
            <div style={bodyStyle} className="space-y-3">
              <p>
                {t(
                  "À partir du moment où vous nous transmettez des Données personnelles au moyen du Site (notamment en passant une commande), vous acceptez d'être lié à la présente Politique de confidentialité et nous autorisez à traiter vos Données personnelles en conformité avec celle-ci.",
                  "By transmitting Personal Data to us through the Website (in particular by placing an order), you accept this Privacy Policy and authorise us to process your Personal Data in accordance with it."
                )}
              </p>
              <p>
                {t(
                  "Nous nous réservons le droit de modifier la Politique de confidentialité à notre seule discrétion et en tout temps. Le cas échéant, la Politique amendée sera publiée sur le Site avec une date de publication actualisée. Nous vous conseillons de consulter régulièrement la Politique de confidentialité pour prendre connaissance de la version en vigueur.",
                  "We reserve the right to modify this Privacy Policy at our sole discretion and at any time. Where applicable, the amended Privacy Policy will be published on the Website with an updated publication date. We advise you to regularly consult the Privacy Policy to take note of the version in force."
                )}
              </p>
              <p>
                {t(
                  "En cas de modification affectant vos droits de manière substantielle, votre consentement sera requis. En cas de désaccord, vous devez renoncer à utiliser le Site et les services associés.",
                  "In the event of a modification that substantially affects your rights, your consent will be required. In the event of disagreement, you must refrain from using the Website and its associated services."
                )}
              </p>
            </div>
          </section>

          {/* IV. Responsable du Traitement des Données */}
          <section>
            <h2 className="mb-4 tracking-[0.2em] uppercase" style={sectionTitleStyle}>
              {t("IV. Responsable du Traitement des Données", "IV. Data Controller")}
            </h2>
            <div style={bodyStyle}>
              <p className="mb-4">{t("Le responsable du Traitement est :", "The Data Controller is:")}</p>
              <p className="mb-1">Frédéric Haenni | Clips'ON</p>
              <p className="mb-1">{t("Entreprise individuelle de droit suisse", "Sole proprietorship under Swiss law")}</p>
              <p className="mb-1">{t("CH-1206 Genève, Suisse", "CH-1206 Geneva, Switzerland")}</p>
              <p className="mb-4">{t("Email : info@clips-on.ch", "Email: info@clips-on.ch")}</p>
              <p className="mb-3">
                {t(
                  "Nous collectons, traitons et faisons usage de vos Données pour les finalités déterminées au chapitre VI de la présente Politique.",
                  "We collect, process, and use your Data for the purposes set out in Chapter VI of this Policy."
                )}
              </p>
              <p>
                {t(
                  "Toute question ou demande relative au Traitement de vos Données personnelles, notamment relative à l'exercice de vos droits, peut nous être adressée à l'adresse électronique info@clips-on.ch ou par courrier postal à l'adresse ci-dessus.",
                  "Any question or request relating to the Processing of your Personal Data, including the exercise of your rights, may be sent to us at info@clips-on.ch or by post to the address above."
                )}
              </p>
            </div>
          </section>

          {/* V. Principes de protection des Données */}
          <section>
            <h2 className="mb-4 tracking-[0.2em] uppercase" style={sectionTitleStyle}>
              {t("V. Principes de protection des Données", "V. Data protection principles")}
            </h2>
            <div style={bodyStyle} className="space-y-3">
              <p>
                {t(
                  "Le Traitement de vos Données personnelles par Clips'ON est soumis à la Loi fédérale suisse sur la protection des données du 25 septembre 2020 (RS 235.1 ; nLPD), entrée en vigueur le 1er septembre 2023.",
                  "The Processing of your Personal Data by Clips'ON is subject to the Swiss Federal Act on Data Protection of 25 September 2020 (SR 235.1; nFADP), which came into force on 1 September 2023."
                )}
              </p>
              <p>
                {t(
                  "Pour les Utilisateurs résidant dans l'Union européenne, le Règlement général sur la protection des données (RGPD — Règlement UE 2016/679) s'applique également.",
                  "For Users residing in the European Union, the General Data Protection Regulation (GDPR — Regulation EU 2016/679) also applies."
                )}
              </p>
            </div>
          </section>

          {/* VI. Traitement des Données */}
          <section>
            <h2 className="mb-4 tracking-[0.2em] uppercase" style={sectionTitleStyle}>
              {t("VI. Traitement des Données", "VI. Data Processing")}
            </h2>
            <div style={bodyStyle} className="space-y-6">
              {/* A. Données de commande */}
              <div>
                <h3 className="font-normal mb-3" style={{ color: '#0D0C0B', fontSize: '18px', letterSpacing: '2px' }}>
                  {t("A. Données de commande", "A. Order data")}
                </h3>
                <p className="mb-3">
                  {t(
                    "Lorsque vous passez une commande sur clips-on.ch, les informations suivantes sont collectées :",
                    "When you place an order on clips-on.ch, the following information is collected:"
                  )}
                </p>
                <ul className="list-disc pl-6 space-y-1 mb-3">
                  <li>{t("nom complet*", "full name*")}</li>
                  <li>{t("adresse email*", "email address*")}</li>
                  <li>{t("adresse postale complète* (rue, code postal, ville, pays)", "full postal address* (street, postcode, city, country)")}</li>
                </ul>
                <p className="mb-3">
                  {t(
                    "Les termes accompagnés d'un astérisque sont obligatoires pour le traitement de votre commande.",
                    "Items marked with an asterisk are mandatory for processing your order."
                  )}
                </p>
                <p className="mb-3">{t("Ces Données sont notamment traitées pour :", "This Data is processed for:")}</p>
                <ul className="list-disc pl-6 space-y-1 mb-3">
                  <li>{t("le traitement et le suivi de vos commandes", "processing and tracking your orders")}</li>
                  <li>{t("l'expédition des Produits à l'adresse indiquée", "shipping Products to the address provided")}</li>
                  <li>{t("la communication relative à votre commande (confirmation, suivi d'expédition)", "order-related communication (confirmation, shipping tracking)")}</li>
                  <li>{t("le respect de nos obligations légales et comptables", "compliance with our legal and accounting obligations")}</li>
                </ul>
                <p>
                  {t(
                    "La base juridique de ce Traitement est l'exécution du contrat de vente (votre commande) et nos obligations légales.",
                    "The legal basis for this Processing is the performance of the sales contract (your order) and our legal obligations."
                  )}
                </p>
              </div>

              {/* B. Données relatives à l'utilisation du Site */}
              <div>
                <h3 className="font-normal mb-3" style={{ color: '#0D0C0B', fontSize: '18px', letterSpacing: '2px' }}>
                  {t("B. Données relatives à l'utilisation du Site", "B. Website usage data")}
                </h3>
                <p className="mb-3">
                  {t(
                    "Lorsque vous utilisez le Site, Clips'ON peut être amenée à traiter, en sus, les informations suivantes :",
                    "When you use the Website, Clips'ON may additionally process the following information:"
                  )}
                </p>
                <ul className="list-disc pl-6 space-y-1 mb-3">
                  <li>{t("votre adresse IP", "your IP address")}</li>
                  <li>{t("le type d'appareil que vous utilisez", "the type of device you are using")}</li>
                  <li>{t("votre système d'exploitation et sa version", "your operating system and its version")}</li>
                  <li>{t("le navigateur utilisé", "the browser used")}</li>
                  <li>{t("le type de plateforme", "the type of platform")}</li>
                  <li>{t("le temps que vous passez sur le Site", "the time you spend on the Website")}</li>
                  <li>{t("les pages que vous consultez sur le Site (notamment les chemins et sources référentes)", "the pages you visit on the Website (including paths and referring sources)")}</li>
                  <li>{t("la fréquence et les circonstances d'utilisation", "frequency and circumstances of use")}</li>
                </ul>
                <p className="mb-3">{t("Ces Données sont traitées aux fins de :", "This Data is processed for the purposes of:")}</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>
                    {t(
                      "développer, améliorer, personnaliser et administrer le Site, notamment en fonction de l'utilisation qui en est faite par les Utilisateurs",
                      "developing, improving, personalising, and administering the Website, based on how Users interact with it"
                    )}
                  </li>
                  <li>{t("assurer la sécurité du Site", "ensuring the security of the Website")}</li>
                  <li>{t("détecter d'éventuelles fraudes ou actes malveillants", "detecting potential fraud or malicious activity")}</li>
                  <li>{t("améliorer le fonctionnement technique du Site", "improving the technical operation of the Website")}</li>
                  <li>{t("satisfaire à des obligations légales ou réglementaires", "complying with legal or regulatory obligations")}</li>
                </ul>
              </div>

              {/* C. Cookies */}
              <div>
                <h3 className="font-normal mb-3" style={{ color: '#0D0C0B', fontSize: '18px', letterSpacing: '2px' }}>
                  C. Cookies
                </h3>
                <p className="mb-3">
                  {t(
                    "Lorsque vous utilisez le Site, et en fonction des paramètres choisis, certaines informations stockées sous la forme de cookies temporaires (qui expirent dès la fermeture de votre navigateur) ou permanents (qui demeurent sur votre disque dur après déconnexion) sont susceptibles d'être collectées par Clips'ON et/ou par des tiers.",
                    "When you use the Website, and depending on the settings chosen, certain information stored in the form of temporary cookies (which expire when you close your browser) or persistent cookies (which remain on your hard drive after disconnection) may be collected by Clips'ON and/or third parties."
                  )}
                </p>
                <p className="mb-3">
                  {t(
                    "Il peut notamment s'agir de votre adresse IP, du type de navigateur utilisé, des pages entrantes et sortantes, du type de système d'exploitation utilisé, des dates et heures de visite du Site, etc.",
                    "This may include your IP address, browser type, entry and exit pages, operating system type, visit dates and times, etc."
                  )}
                </p>
                <p className="mb-3">
                  {t(
                    "Lorsque vous naviguez sur le Site pour la première fois, un bandeau vous informe des finalités des cookies et vous permet de donner ou refuser votre consentement.",
                    "When you first visit the Website, a banner informs you of the purposes of cookies and allows you to give or refuse your consent."
                  )}
                </p>
                <p className="mb-3">
                  <strong>{t("Cookies essentiels", "Essential cookies")} :</strong>{" "}
                  {t(
                    "certains cookies sont strictement nécessaires au bon fonctionnement du Site et au traitement de vos commandes via Stripe. Ils ne requièrent pas votre consentement.",
                    "certain cookies are strictly necessary for the proper functioning of the Website and for processing your orders via Stripe. They do not require your consent."
                  )}
                </p>
                <p className="mb-3">
                  <strong>{t("Cookies analytiques (Google Analytics)", "Analytical cookies (Google Analytics)")} :</strong>{" "}
                  {t(
                    "le Site utilise Google Analytics, un outil d'analyse fourni par Google LLC (Mountain View, Californie, USA). Les cookies utilisés par Google Analytics collectent notamment votre adresse IP et des informations relatives à votre navigation sur le Site (pages visitées, durée des sessions, etc.). Ces cookies ne sont activés qu'après obtention de votre consentement via le bandeau cookies.",
                    "the Website uses Google Analytics, an analytics tool provided by Google LLC (Mountain View, California, USA). Google Analytics cookies collect your IP address and information about your browsing activity on the Website (pages visited, session duration, etc.). These cookies are only activated after obtaining your consent via the cookie banner."
                  )}
                </p>
                <p className="mb-2">
                  {t("Vous pouvez désactiver Google Analytics à partir de la page suivante :", "You can opt out of Google Analytics at:")}{" "}
                  <a
                    href="https://tools.google.com/dlpage/gaoptout"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline transition-colors"
                    style={{ color: '#E1593F' }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#C4462E')}
                    onMouseLeave={e => (e.currentTarget.style.color = '#E1593F')}
                  >
                    https://tools.google.com/dlpage/gaoptout
                  </a>
                </p>
                <p>
                  {t("Pour en savoir plus sur l'utilisation des cookies de Google, veuillez consulter la page suivante :", "To learn more about Google's use of cookies, please visit:")}{" "}
                  <a
                    href="https://policies.google.com/technologies/types"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline transition-colors"
                    style={{ color: '#E1593F' }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#C4462E')}
                    onMouseLeave={e => (e.currentTarget.style.color = '#E1593F')}
                  >
                    https://policies.google.com/technologies/types
                  </a>
                </p>
              </div>

              {/* D. Autres données personnelles */}
              <div>
                <h3 className="font-normal mb-3" style={{ color: '#0D0C0B', fontSize: '18px', letterSpacing: '2px' }}>
                  {t("D. Autres données personnelles", "D. Other personal data")}
                </h3>
                <p>
                  {t(
                    "Clips'ON peut être amenée à traiter toute autre information ou Données personnelles que vous lui transmettez volontairement par le biais du Site ou par email, notamment pour pouvoir répondre à vos questions ou sollicitations.",
                    "Clips'ON may process any other information or Personal Data that you voluntarily transmit via the Website or by email, in particular to respond to your questions or requests."
                  )}
                </p>
              </div>

              {/* E. Autres finalités du Traitement */}
              <div>
                <h3 className="font-normal mb-3" style={{ color: '#0D0C0B', fontSize: '18px', letterSpacing: '2px' }}>
                  {t("E. Autres finalités du Traitement", "E. Other processing purposes")}
                </h3>
                <p className="mb-3">
                  {t(
                    "De manière générale, Clips'ON a le droit de traiter vos Données personnelles à des fins autres que celles expressément mentionnées dans la présente Politique lorsque le Traitement en cause est nécessaire à la sauvegarde d'un intérêt privé ou public prépondérant. Tel peut notamment être le cas lorsque le Traitement est nécessaire pour :",
                    "Generally, Clips'ON has the right to process your Personal Data for purposes other than those expressly mentioned in this Policy where such Processing is necessary to safeguard an overriding private or public interest. This may be the case where Processing is necessary to:"
                  )}
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>
                    {t(
                      "la sauvegarde ou l'exercice des droits de Clips'ON, par exemple en cas de litige ou de réclamation",
                      "safeguard or exercise Clips'ON's rights, for example in the event of a dispute or claim"
                    )}
                  </li>
                  <li>
                    {t(
                      "la protection de Clips'ON, de son infrastructure, ou de tiers contre des actes illicites ou malveillants",
                      "protect Clips'ON, its infrastructure, or third parties against unlawful or malicious acts"
                    )}
                  </li>
                  <li>
                    {t(
                      "répondre à une obligation légale ou réglementaire (obligation de collaborer avec une autorité administrative, etc.)",
                      "comply with a legal or regulatory obligation (e.g. cooperation with an administrative authority)"
                    )}
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* VII. Partage des Données */}
          <section>
            <h2 className="mb-4 tracking-[0.2em] uppercase" style={sectionTitleStyle}>
              {t("VII. Partage des Données", "VII. Data sharing")}
            </h2>
            <div style={bodyStyle}>
              <p className="mb-3">
                {t(
                  "Vos Données personnelles sont traitées par Clips'ON conformément à la présente Politique et sont également susceptibles d'être communiquées aux destinataires externes suivants :",
                  "Your Personal Data is processed by Clips'ON in accordance with this Policy and may also be shared with the following external recipients:"
                )}
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-3">
                <li>
                  {t(
                    "Stripe, Inc. (San Francisco, USA / Stripe Payments Europe, Ltd., Dublin, Irlande), pour le traitement sécurisé des paiements",
                    "Stripe, Inc. (San Francisco, USA / Stripe Payments Europe, Ltd., Dublin, Ireland), for secure payment processing"
                  )}{" "}
                  —{" "}
                  {t("Politique de confidentialité", "Privacy policy")} :{" "}
                  <a
                    href="https://stripe.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline transition-colors"
                    style={{ color: '#E1593F' }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#C4462E')}
                    onMouseLeave={e => (e.currentTarget.style.color = '#E1593F')}
                  >
                    https://stripe.com/privacy
                  </a>
                </li>
                <li>{t("La Poste / services postaux, pour l'expédition de vos commandes", "La Poste / postal services, for shipping your orders")}</li>
                <li>
                  {t(
                    "Google LLC (Mountain View, USA), pour l'analyse de la fréquentation du Site via Google Analytics, sous réserve de votre consentement",
                    "Google LLC (Mountain View, USA), for Website traffic analysis via Google Analytics, subject to your consent"
                  )}
                </li>
                <li>{t("Infomaniak Network SA (Genève, Suisse), hébergeur du Site", "Infomaniak Network SA (Geneva, Switzerland), Website hosting provider")}</li>
                <li>
                  {t(
                    "autorités administratives et judiciaires compétentes, afin de permettre à Clips'ON de remplir ses obligations légales, d'appliquer ou faire appliquer ses accords avec vous, ou de protéger les droits, la propriété ou la sécurité de Clips'ON et de tiers",
                    "competent administrative and judicial authorities, to enable Clips'ON to fulfil its legal obligations, enforce its agreements with you, or protect the rights, property, or safety of Clips'ON and third parties"
                  )}
                </li>
                <li>
                  {t(
                    "auxiliaires de justice dans le cadre de leur mission de recouvrement de créances, le cas échéant",
                    "legal auxiliaries in the context of debt recovery, where applicable"
                  )}
                </li>
              </ul>
              <p>
                {t(
                  "Vos Données ne sont jamais vendues ni cédées à des tiers à des fins commerciales.",
                  "Your Data is never sold or transferred to third parties for commercial purposes."
                )}
              </p>
            </div>
          </section>

          {/* VIII. Sous-traitants */}
          <section>
            <h2 className="mb-4 tracking-[0.2em] uppercase" style={sectionTitleStyle}>
              {t("VIII. Sous-traitants", "VIII. Sub-processors")}
            </h2>
            <div style={bodyStyle}>
              <p className="mb-3">
                {t(
                  "Vos Données personnelles peuvent être rendues accessibles ou transmises à des sous-traitants chargés par Clips'ON d'accomplir certaines tâches relatives à ses activités (fournisseurs de services techniques, d'hébergement, de paiement, de livraison, etc.).",
                  "Your Personal Data may be made accessible or transferred to sub-processors entrusted by Clips'ON with performing certain tasks related to its activities (technical service providers, hosting, payment, delivery, etc.)."
                )}
              </p>
              <p className="mb-3">
                {t(
                  "Dans ce cas, nous nous assurons que ces sous-traitants offrent des garanties suffisantes en matière de confidentialité et de protection de vos Données personnelles, notamment qu'ils ne traitent pas vos Données à leurs propres fins ou à des fins autres que celles évoquées dans la présente Politique.",
                  "In such cases, we ensure that these sub-processors provide sufficient guarantees regarding the confidentiality and protection of your Personal Data, and that they do not process your Data for their own purposes or for purposes other than those set out in this Policy."
                )}
              </p>
              <p className="mb-3">{t("Clips'ON fait notamment appel aux sous-traitants suivants :", "Clips'ON uses the following sub-processors:")}</p>
              <ul className="list-disc pl-6 space-y-2 mb-3">
                <li>
                  {t(
                    "Stripe, Inc. (USA/Irlande), pour le traitement des paiements en ligne. Les données de paiement sont traitées directement par Stripe et ne transitent pas par les serveurs de Clips'ON.",
                    "Stripe, Inc. (USA/Ireland), for online payment processing. Payment data is processed directly by Stripe and does not pass through Clips'ON's servers."
                  )}{" "}
                  {t("Politique de confidentialité", "Privacy policy")} :{" "}
                  <a
                    href="https://stripe.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline transition-colors"
                    style={{ color: '#E1593F' }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#C4462E')}
                    onMouseLeave={e => (e.currentTarget.style.color = '#E1593F')}
                  >
                    https://stripe.com/privacy
                  </a>
                </li>
                <li>
                  {t(
                    "Google LLC (USA), exploitant de la solution Google Analytics pour l'analyse de la fréquentation du Site et du comportement de ses Utilisateurs.",
                    "Google LLC (USA), operating Google Analytics for analysing Website traffic and User behaviour."
                  )}{" "}
                  {t("Politique de confidentialité", "Privacy policy")} :{" "}
                  <a
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline transition-colors"
                    style={{ color: '#E1593F' }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#C4462E')}
                    onMouseLeave={e => (e.currentTarget.style.color = '#E1593F')}
                  >
                    https://policies.google.com/privacy
                  </a>
                </li>
                <li>
                  {t(
                    "Infomaniak Network SA (Suisse), pour l'hébergement du Site et des services associés.",
                    "Infomaniak Network SA (Switzerland), for Website hosting and associated services."
                  )}{" "}
                  {t("Politique de confidentialité", "Privacy policy")} :{" "}
                  <a
                    href="https://www.infomaniak.com/fr/cgv/politique-de-confidentialite"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline transition-colors"
                    style={{ color: '#E1593F' }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#C4462E')}
                    onMouseLeave={e => (e.currentTarget.style.color = '#E1593F')}
                  >
                    https://www.infomaniak.com/fr/cgv/politique-de-confidentialite
                  </a>
                </li>
              </ul>
              <p>
                {t(
                  "Il vous est loisible de nous demander, en tout temps, la liste mise à jour des sous-traitants à qui nous avons confié des Données vous concernant.",
                  "You may request from us, at any time, an updated list of sub-processors to whom we have entrusted Data concerning you."
                )}
              </p>
            </div>
          </section>

          {/* IX. Sécurité */}
          <section>
            <h2 className="mb-4 tracking-[0.2em] uppercase" style={sectionTitleStyle}>
              {t("IX. Sécurité", "IX. Security")}
            </h2>
            <div style={bodyStyle}>
              <p className="mb-3">
                {t(
                  "Clips'ON s'engage activement à prendre les mesures de protection physiques, administratives et techniques les plus efficaces, appropriées et commercialement raisonnables, conçues pour protéger vos Données personnelles contre la perte accidentelle et contre l'accès, l'utilisation, l'altération et la divulgation non autorisés.",
                  "Clips'ON is actively committed to implementing the most effective, appropriate, and commercially reasonable physical, administrative, and technical protective measures, designed to protect your Personal Data against accidental loss and against unauthorised access, use, alteration, and disclosure."
                )}
              </p>
              <p className="mb-3">
                {t(
                  "Les transactions de paiement sont entièrement sécurisées par Stripe et ne transitent pas par nos serveurs. Vos données de carte bancaire ne sont jamais collectées ni stockées par Clips'ON.",
                  "Payment transactions are fully secured by Stripe and do not pass through our servers. Your bank card details are never collected or stored by Clips'ON."
                )}
              </p>
              <p>
                {t(
                  "Nos prestataires de services sont soumis à des obligations de confidentialité et tenus de respecter les dispositions applicables en matière de protection des données.",
                  "Our service providers are subject to confidentiality obligations and required to comply with applicable data protection provisions."
                )}
              </p>
            </div>
          </section>

          {/* X. Droits des Utilisateurs */}
          <section>
            <h2 className="mb-4 tracking-[0.2em] uppercase" style={sectionTitleStyle}>
              {t("X. Droits des Utilisateurs", "X. User rights")}
            </h2>
            <div style={bodyStyle}>
              <p className="mb-3">
                {t(
                  "Conformément à la Loi fédérale suisse sur la protection des données (nLPD) et, pour les résidents de l'Union européenne, au Règlement général sur la protection des données (RGPD), vous avez le droit, en tout temps et sans indication de motif, de :",
                  "In accordance with the Swiss Federal Act on Data Protection (nFADP) and, for residents of the European Union, the General Data Protection Regulation (GDPR), you have the right, at any time and without stating reasons, to:"
                )}
              </p>
              <ul className="list-disc pl-6 space-y-1 mb-3">
                <li>{t("demander quelles Données vous concernant sont traitées par Clips'ON", "request information about which Data concerning you is processed by Clips'ON")}</li>
                <li>{t("recevoir une copie de vos Données personnelles", "receive a copy of your Personal Data")}</li>
                <li>{t("demander la rectification de vos Données personnelles inexactes", "request the rectification of inaccurate Personal Data")}</li>
                <li>
                  {t(
                    "demander l'effacement de vos Données personnelles (sous réserve des Données que Clips'ON a un intérêt prépondérant ou une obligation légale à conserver)",
                    "request the erasure of your Personal Data (subject to Data that Clips'ON has an overriding interest or legal obligation to retain)"
                  )}
                </li>
                <li>{t("demander la limitation du Traitement", "request the restriction of Processing")}</li>
                <li>{t("demander la portabilité de vos Données", "request the portability of your Data")}</li>
                <li>{t("révoquer le consentement donné pour un ou des Traitements particuliers", "revoke consent given for one or more specific Processing operations")}</li>
                <li>{t("vous opposer au Traitement de vos Données", "object to the Processing of your Data")}</li>
              </ul>
              <p className="mb-3">
                {t(
                  "Ces démarches sont gratuites, à moins qu'elles ne nécessitent un effort particulier, et doivent nous être adressées à l'adresse électronique info@clips-on.ch ou par courrier postal.",
                  "These steps are free of charge, unless they require a particular effort, and should be addressed to us at info@clips-on.ch or by post."
                )}
              </p>
              <p>
                {t(
                  "Nous donnons suite rapidement à ces demandes, en principe dans un délai de 30 (trente) jours dès réception.",
                  "We will respond promptly to such requests, in principle within 30 (thirty) days of receipt."
                )}
              </p>
            </div>
          </section>

          {/* XI. Durée de conservation */}
          <section>
            <h2 className="mb-4 tracking-[0.2em] uppercase" style={sectionTitleStyle}>
              {t("XI. Durée de conservation", "XI. Data retention")}
            </h2>
            <div style={bodyStyle}>
              <p className="mb-3">
                {t(
                  "Vos Données personnelles de commande sont conservées pendant une durée maximale de 3 ans à compter de votre dernière commande, sauf obligation légale de conservation plus longue (notamment comptable : 10 ans, conformément au Code des obligations suisse).",
                  "Your order-related Personal Data is retained for a maximum of 3 years from your last order, unless a longer retention period is required by law (in particular for accounting purposes: 10 years, in accordance with the Swiss Code of Obligations)."
                )}
              </p>
              <p className="mb-3">
                {t(
                  "Les données d'utilisation collectées via Google Analytics sont conservées conformément aux paramètres configurés et à la politique de conservation de Google.",
                  "Usage data collected via Google Analytics is retained in accordance with the configured settings and Google's retention policy."
                )}
              </p>
              <p>
                {t(
                  "Si vous souhaitez que Clips'ON efface vos Données personnelles avant l'expiration des délais prévus ci-dessus, il vous incombe de nous adresser une demande d'effacement à info@clips-on.ch.",
                  "If you wish Clips'ON to erase your Personal Data before the expiry of the periods set out above, you must send us an erasure request at info@clips-on.ch."
                )}
              </p>
            </div>
          </section>

          {/* XII. Droit applicable et for */}
          <section>
            <h2 className="mb-4 tracking-[0.2em] uppercase" style={sectionTitleStyle}>
              {t("XII. Droit applicable et for", "XII. Applicable law and jurisdiction")}
            </h2>
            <div style={bodyStyle}>
              <p className="mb-3">
                {t(
                  "La présente Politique de confidentialité, ainsi que tout litige en lien direct ou indirect avec le Traitement, par Clips'ON, de Données personnelles collectées par le biais du Site, sont soumis au droit suisse, à l'exclusion des règles en matière de conflit de lois.",
                  "This Privacy Policy, as well as any dispute directly or indirectly related to the Processing by Clips'ON of Personal Data collected through the Website, is governed by Swiss law, to the exclusion of conflict of laws rules."
                )}
              </p>
              <p className="mb-3">
                {t(
                  "Tout litige en lien avec le Traitement par Clips'ON de Données personnelles collectées par le biais du Site relève de la seule compétence des tribunaux ordinaires de la République et Canton de Genève, un recours au Tribunal fédéral étant réservé.",
                  "Any dispute related to the Processing by Clips'ON of Personal Data collected through the Website falls under the exclusive jurisdiction of the ordinary courts of the Republic and Canton of Geneva, with recourse to the Federal Tribunal being reserved."
                )}
              </p>
              <p>
                {t(
                  "Pour les consommateurs résidant dans l'Union européenne, la plateforme de règlement en ligne des litiges de la Commission européenne est accessible à l'adresse suivante :",
                  "For consumers residing in the European Union, the European Commission's online dispute resolution platform is available at:"
                )}{" "}
                <a
                  href="https://ec.europa.eu/consumers/odr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline transition-colors"
                  style={{ color: '#E1593F' }}
                  onMouseEnter={e => (e.currentTarget.style.color = '#C4462E')}
                  onMouseLeave={e => (e.currentTarget.style.color = '#E1593F')}
                >
                  https://ec.europa.eu/consumers/odr
                </a>
              </p>
            </div>
          </section>

          {/* Contact */}
          <section>
            <h2 className="mb-4 tracking-[0.2em] uppercase" style={sectionTitleStyle}>
              Contact
            </h2>
            <div style={bodyStyle}>
              <p className="mb-2">
                {t(
                  "Pour toute question relative à vos données personnelles :",
                  "For any questions regarding your personal data:"
                )}
              </p>
              <p className="mb-1">Frédéric Haenni | Clips'ON</p>
              <p className="mb-1">{t("CH-1206 Genève, Suisse", "CH-1206 Geneva, Switzerland")}</p>
              <p>
                {t("Email : ", "Email: ")}
                <a
                  href="mailto:info@clips-on.ch"
                  className="underline transition-colors"
                  style={{ color: '#E1593F' }}
                  onMouseEnter={e => (e.currentTarget.style.color = '#C4462E')}
                  onMouseLeave={e => (e.currentTarget.style.color = '#E1593F')}
                >
                  info@clips-on.ch
                </a>
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
